import { useState } from 'react';
import type { BarDuration, TradeLogicSequenceStepTimingV2, TradeLogicTemporalUnitV2 } from '@einhaus/project314-types';
import Button from '@/components/UI/Button';
import FormCheckbox from '@/components/UI/FormCheckbox';
import FormInput from '@/components/UI/FormInput';
import FormSelect from '@/components/UI/FormSelect';

const durations: BarDuration[] = ['1m', '3m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d', '1w', '1mo'];
const units: { value: TradeLogicTemporalUnitV2; label: string }[] = [
    { value: 'sessions', label: 'Trading days (skip market holidays)' },
    { value: 'bars', label: 'Completed bars' },
    { value: 'minutes', label: 'Elapsed minutes' },
    { value: 'hours', label: 'Elapsed hours' },
    { value: 'days', label: 'Elapsed 24-hour periods' },
    { value: 'seconds', label: 'Elapsed seconds' }
];

export default function SequenceStepTimingEditor({
    label,
    timing,
    first,
    onChange
}: {
    label: string;
    timing?: TradeLogicSequenceStepTimingV2;
    first: boolean;
    onChange: (value: TradeLogicSequenceStepTimingV2) => void;
}) {
    const [trigger, setTrigger] = useState<BarDuration | null>(timing?.triggerDuration ?? null);
    const [limited, setLimited] = useState(Boolean(timing?.within));
    const [unit, setUnit] = useState<TradeLogicTemporalUnitV2>(timing?.within?.unit ?? 'sessions');
    const [duration, setDuration] = useState<BarDuration>(timing?.within?.duration ?? '1d');
    const [minimum, setMinimum] = useState(String(timing?.within?.minimum ?? 1));
    const [maximum, setMaximum] = useState(String(timing?.within?.value ?? 5));
    const [error, setError] = useState<string | null>(null);
    const apply = () => {
        const min = Number(minimum);
        const max = Number(maximum);
        if (
            !first &&
            limited &&
            (!minimum.trim() ||
                !maximum.trim() ||
                !Number.isSafeInteger(min) ||
                !Number.isSafeInteger(max) ||
                min < 0 ||
                min > max ||
                max > 1000000)
        ) {
            setError('Enter whole numbers with 0 ≤ minimum ≤ maximum ≤ 1,000,000.');
            return;
        }
        onChange({
            triggerDuration: trigger,
            within:
                first || !limited
                    ? null
                    : {
                          unit,
                          minimum: min,
                          value: max,
                          duration: unit === 'bars' ? duration : null
                      }
        });
        setError(null);
    };
    return (
        <fieldset className="space-y-3 rounded border border-zinc-700 p-3">
            <legend className="px-1 text-sm">{label}</legend>
            <FormSelect
                label="Check when a new candle closes"
                value={trigger ?? ''}
                options={[{ value: '', label: 'Use the condition’s timeframe' }, ...durations.map((value) => ({ value, label: value }))]}
                onChange={(event) => setTrigger(durations.find((value) => value === event.target.value) ?? null)}
            />
            {!first && (
                <>
                    <FormCheckbox
                        label="Limit waiting period after the preceding step"
                        checked={limited}
                        onChange={(event) => setLimited(event.target.checked)}
                    />
                    {limited && (
                        <>
                            <FormSelect
                                label="Count the wait in"
                                value={unit}
                                options={units}
                                onChange={(event) => {
                                    const selected = units.find((value) => value.value === event.target.value);
                                    if (selected) setUnit(selected.value);
                                }}
                            />
                            {unit === 'bars' && (
                                <FormSelect
                                    label="Bar timeframe for counting the wait"
                                    value={duration}
                                    options={durations.map((value) => ({ value, label: value }))}
                                    onChange={(event) => {
                                        const selected = durations.find((value) => value === event.target.value);
                                        if (selected) setDuration(selected);
                                    }}
                                />
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <FormInput
                                    label="Minimum wait (inclusive)"
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={minimum}
                                    onChange={(event) => setMinimum(event.target.value)}
                                />
                                <FormInput
                                    label="Maximum wait (inclusive)"
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={maximum}
                                    onChange={(event) => setMaximum(event.target.value)}
                                />
                            </div>
                            <p className="text-xs text-zinc-400">
                                The preceding step’s trading day is day 0. Early candles are ignored; the setup expires after the maximum
                                wait.
                            </p>
                        </>
                    )}
                </>
            )}
            {error && (
                <p role="alert" className="text-sm text-red-400">
                    {error}
                </p>
            )}
            <Button onClick={apply}>Apply step timing</Button>
        </fieldset>
    );
}
